import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  Avatar,
  Card,
} from "@mui/material";
import { firestore } from "../Firebase";
import Bg from "../Images/about.jpg";
import { Delete, Edit, CloudUpload, Person, Add } from "@mui/icons-material";
import { motion } from "framer-motion";
import { FaUsers, FaUserTie } from "react-icons/fa";

// Cloudinary configuration
const CLOUDINARY_CLOUD_NAME = "dzhmnzqfb";
const CLOUDINARY_UPLOAD_PRESET = "Ictaz_upload";

const AdminExecutives = () => {
  const [executives, setExecutives] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [feedbackSeverity, setFeedbackSeverity] = useState("success");

  const [currentExecutive, setCurrentExecutive] = useState(null);
  const [name, setName] = useState("");
  const [position, setPosition] = useState("");
  const [order, setOrder] = useState(1);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [uploading, setUploading] = useState(false);

  const darkGlassStyle = {
    background: "rgba(0, 0, 36, 0.85)",
    backdropFilter: "blur(20px)",
    border: "1px solid rgba(0, 217, 255, 0.2)",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4)",
  };

  const inputStyle = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255, 255, 255, 0.05)",
      borderRadius: "12px",
      color: "white",
      "& fieldset": { borderColor: "rgba(0, 217, 255, 0.3)" },
      "&:hover fieldset": { borderColor: "rgba(0, 217, 255, 0.5)" },
      "&.Mui-focused fieldset": { borderColor: "#00d9ff" },
    },
    "& .MuiInputLabel-root": { color: "rgba(255, 255, 255, 0.7)" },
    "& .MuiInputLabel-root.Mui-focused": { color: "#00d9ff" },
    "& .MuiSelect-icon": { color: "rgba(255, 255, 255, 0.7)" },
  };

  const positionOptions = [
    "President",
    "Vice President",
    "Secretary General",
    "Academic Secretary",
    "Treasurer General",
    "Research and Consultancy",
    "Project Coordinator",
    "Information Publicity Secretary",
    "Committee Member",
  ];

  useEffect(() => {
    const loadExecutives = async () => {
      try {
        const snapshot = await firestore
          .collection("executives")
          .orderBy("order", "asc")
          .get();
        const data = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setExecutives(data);
      } catch (error) {
        console.error("Error fetching executives: ", error);
        showFeedback("Error fetching executives", "error");
      }
    };
    loadExecutives();
  }, []);

  const showFeedback = (message, severity = "success") => {
    setFeedbackMessage(message);
    setFeedbackSeverity(severity);
    setFeedbackOpen(true);
  };

  const resetForm = () => {
    setName("");
    setPosition("");
    setOrder(executives.length + 1);
    setImageFile(null);
    setImagePreview("");
    setCurrentExecutive(null);
  };

  const openAddDialog = () => {
    resetForm();
    setOrder(executives.length + 1);
    setOpen(true);
  };

  const closeAddDialog = () => {
    resetForm();
    setOpen(false);
  };

  const openEditDialog = (executive) => {
    setCurrentExecutive(executive);
    setName(executive.name);
    setPosition(executive.position);
    setOrder(executive.order || 1);
    setImagePreview(executive.imageUrl || "");
    setImageFile(null);
    setEditOpen(true);
  };

  const closeEditDialog = () => {
    resetForm();
    setEditOpen(false);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        showFeedback("Please select an image file", "error");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        showFeedback("Image size must be less than 5MB", "error");
        return;
      }
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const uploadImage = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", CLOUDINARY_UPLOAD_PRESET);
    formData.append("folder", "executives");

    const response = await fetch(
      `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
      { method: "POST", body: formData }
    );

    if (!response.ok) throw new Error("Failed to upload image");
    const data = await response.json();
    return data.secure_url;
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);

    try {
      let imageUrl = "";
      if (imageFile) imageUrl = await uploadImage(imageFile);

      const newExecutive = {
        name,
        position,
        order: parseInt(order),
        imageUrl,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      const docRef = await firestore.collection("executives").add(newExecutive);
      setExecutives(
        [...executives, { id: docRef.id, ...newExecutive }].sort((a, b) => a.order - b.order)
      );

      closeAddDialog();
      showFeedback("Executive added successfully!");
    } catch (error) {
      console.error("Error adding executive:", error);
      showFeedback("Error adding executive", "error");
    } finally {
      setUploading(false);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);

    try {
      let imageUrl = currentExecutive.imageUrl || "";
      if (imageFile) imageUrl = await uploadImage(imageFile);

      const updatedExecutive = {
        name,
        position,
        order: parseInt(order),
        imageUrl,
        updatedAt: new Date(),
      };

      await firestore.collection("executives").doc(currentExecutive.id).update(updatedExecutive);

      const updatedExecutives = executives
        .map((exec) => (exec.id === currentExecutive.id ? { ...exec, ...updatedExecutive } : exec))
        .sort((a, b) => a.order - b.order);

      setExecutives(updatedExecutives);
      closeEditDialog();
      showFeedback("Executive updated successfully!");
    } catch (error) {
      console.error("Error updating executive:", error);
      showFeedback("Error updating executive", "error");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (executive) => {
    if (!window.confirm(`Are you sure you want to delete ${executive.name}?`)) return;

    try {
      await firestore.collection("executives").doc(executive.id).delete();
      setExecutives(executives.filter((exec) => exec.id !== executive.id));
      showFeedback("Executive deleted successfully!");
    } catch (error) {
      console.error("Error deleting executive:", error);
      showFeedback("Error deleting executive", "error");
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, rgba(0, 0, 36, 0.95) 0%, rgba(10, 25, 47, 0.9) 50%, rgba(0, 0, 36, 0.95) 100%), url(${Bg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
        color: "white",
        pt: { xs: 12, md: 14 },
        pb: 8,
      }}
    >
      <Container maxWidth="xl">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
            <Box sx={{ width: 60, height: 60, borderRadius: "15px", background: "linear-gradient(135deg, #00d9ff, #00b4d8)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <FaUsers size={30} color="white" />
            </Box>
            <Box>
              <Typography variant="h4" fontWeight={800} sx={{ color: 'white' }}>Executive Management</Typography>
              <Typography variant="body1" sx={{ color: "rgba(255,255,255,0.7)" }}>Manage the executive team displayed on the public website</Typography>
            </Box>
          </Box>
        </motion.div>

        {/* Action Button */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}>
          <Box sx={{ my: 3 }}>
            <Button onClick={openAddDialog} variant="contained" startIcon={<Add />}
              sx={{ borderRadius: "12px", px: 3, py: 1.2, background: "linear-gradient(135deg, #00d9ff, #00b4d8)", fontWeight: 600, "&:hover": { background: "linear-gradient(135deg, #00b4d8, #0096c7)" } }}>
              Add New Executive
            </Button>
          </Box>
        </motion.div>

        {/* Table */}
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }}>
          <Card sx={{ borderRadius: "20px", ...darkGlassStyle, overflow: "hidden" }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ background: "linear-gradient(135deg, rgba(0, 217, 255, 0.2), rgba(0, 180, 216, 0.1))" }}>
                    {["Order", "Photo", "Name", "Position", "Actions"].map((header) => (
                      <TableCell key={header} sx={{ color: "#00d9ff", fontWeight: 700, fontSize: "15px", borderBottom: "1px solid rgba(0, 217, 255, 0.2)" }}>{header}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {executives.length > 0 ? (
                    executives.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((executive) => (
                      <TableRow key={executive.id} sx={{ "&:hover": { background: "rgba(0, 217, 255, 0.05)" }, borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                        <TableCell sx={{ color: "#00d9ff", fontWeight: 600, fontSize: "16px" }}>{executive.order}</TableCell>
                        <TableCell>
                          <Avatar
                            src={executive.imageUrl}
                            alt={executive.name}
                            sx={{ width: 56, height: 56, border: "2px solid rgba(0, 217, 255, 0.3)" }}
                          >
                            {!executive.imageUrl && <Person sx={{ color: "rgba(0, 217, 255, 0.5)" }} />}
                          </Avatar>
                        </TableCell>
                        <TableCell sx={{ color: "white", fontSize: "15px", fontWeight: 500 }}>{executive.name}</TableCell>
                        <TableCell>
                          <Box sx={{ display: "inline-block", px: 2, py: 0.5, borderRadius: "20px", background: "rgba(0, 217, 255, 0.15)", border: "1px solid rgba(0, 217, 255, 0.3)" }}>
                            <Typography variant="body2" sx={{ color: "#00d9ff", fontWeight: 500 }}>{executive.position}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <Button size="small" onClick={() => openEditDialog(executive)}
                              sx={{ borderRadius: "8px", minWidth: "auto", px: 1.5, background: "rgba(16, 185, 129, 0.2)", border: "1px solid #10b981", color: "#10b981", "&:hover": { background: "rgba(16, 185, 129, 0.3)" } }}>
                              <Edit fontSize="small" />
                            </Button>
                            <Button size="small" onClick={() => handleDelete(executive)}
                              sx={{ borderRadius: "8px", minWidth: "auto", px: 1.5, background: "rgba(239, 68, 68, 0.2)", border: "1px solid #ef4444", color: "#ef4444", "&:hover": { background: "rgba(239, 68, 68, 0.3)" } }}>
                              <Delete fontSize="small" />
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ color: "rgba(255,255,255,0.6)", py: 4 }}>
                        No executives found. Add your first executive above.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 15]}
              component="div"
              count={executives.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              sx={{ color: "white", borderTop: "1px solid rgba(0, 217, 255, 0.2)", "& .MuiTablePagination-selectIcon": { color: "white" } }}
            />
          </Card>
        </motion.div>

        {/* Add Executive Dialog */}
        <Dialog open={open} onClose={closeAddDialog} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: "20px", ...darkGlassStyle } }}>
          <DialogTitle sx={{ color: "white", borderBottom: "1px solid rgba(0, 217, 255, 0.2)", display: "flex", alignItems: "center", gap: 2 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: "10px", background: "linear-gradient(135deg, #00d9ff, #00b4d8)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <FaUserTie size={20} color="white" />
            </Box>
            Add New Executive
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <form onSubmit={handleAddSubmit}>
              <Stack spacing={2.5} sx={{ mt: 1 }}>
                {/* Image Upload */}
                <Box sx={{ textAlign: "center" }}>
                  <input accept="image/*" style={{ display: "none" }} id="add-image-upload" type="file" onChange={handleImageChange} />
                  <label htmlFor="add-image-upload">
                    <Box sx={{ width: 120, height: 120, border: "2px dashed rgba(0, 217, 255, 0.4)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", mx: "auto", mb: 1, overflow: "hidden", background: "rgba(0, 217, 255, 0.05)", transition: "all 0.3s ease", "&:hover": { borderColor: "#00d9ff", background: "rgba(0, 217, 255, 0.1)" } }}>
                      {imagePreview ? (
                        <img src={imagePreview} alt="Preview" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      ) : (
                        <CloudUpload sx={{ fontSize: 40, color: "rgba(0, 217, 255, 0.5)" }} />
                      )}
                    </Box>
                  </label>
                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)" }}>Click to upload photo (max 5MB)</Typography>
                </Box>

                <TextField label="Full Name" value={name} onChange={(e) => setName(e.target.value)} required fullWidth sx={inputStyle} />

                <FormControl fullWidth required sx={inputStyle}>
                  <InputLabel>Position</InputLabel>
                  <Select value={position} onChange={(e) => setPosition(e.target.value)} label="Position"
                    MenuProps={{ PaperProps: { sx: { bgcolor: "#0a192f", "& .MuiMenuItem-root": { color: "white", "&:hover": { bgcolor: "rgba(0, 217, 255, 0.2)" } } } } }}>
                    {positionOptions.map((pos) => (<MenuItem key={pos} value={pos}>{pos}</MenuItem>))}
                  </Select>
                </FormControl>

                <TextField label="Display Order" type="number" value={order} onChange={(e) => setOrder(e.target.value)} required fullWidth inputProps={{ min: 1 }} helperText="Lower numbers appear first" sx={inputStyle} FormHelperTextProps={{ sx: { color: "rgba(255,255,255,0.5)" } }} />

                <Button type="submit" variant="contained" fullWidth disabled={uploading}
                  sx={{ py: 1.5, borderRadius: "12px", background: "linear-gradient(135deg, #00d9ff, #00b4d8)", fontWeight: 600, fontSize: "1rem", "&:hover": { background: "linear-gradient(135deg, #00b4d8, #0096c7)" }, "&.Mui-disabled": { background: "rgba(0, 217, 255, 0.3)", color: "rgba(255,255,255,0.5)" } }}>
                  {uploading ? "Uploading..." : "Add Executive"}
                </Button>
              </Stack>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Executive Dialog */}
        <Dialog open={editOpen} onClose={closeEditDialog} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: "20px", ...darkGlassStyle } }}>
          <DialogTitle sx={{ color: "white", borderBottom: "1px solid rgba(0, 217, 255, 0.2)", display: "flex", alignItems: "center", gap: 2 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: "10px", background: "linear-gradient(135deg, #10b981, #059669)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Edit sx={{ color: "white" }} />
            </Box>
            Edit Executive
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <form onSubmit={handleEditSubmit}>
              <Stack spacing={2.5} sx={{ mt: 1 }}>
                {/* Image Upload */}
                <Box sx={{ textAlign: "center" }}>
                  <input accept="image/*" style={{ display: "none" }} id="edit-image-upload" type="file" onChange={handleImageChange} />
                  <label htmlFor="edit-image-upload">
                    <Box sx={{ width: 120, height: 120, border: "2px dashed rgba(0, 217, 255, 0.4)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", mx: "auto", mb: 1, overflow: "hidden", background: "rgba(0, 217, 255, 0.05)", transition: "all 0.3s ease", "&:hover": { borderColor: "#00d9ff", background: "rgba(0, 217, 255, 0.1)" } }}>
                      {imagePreview ? (
                        <img src={imagePreview} alt="Preview" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      ) : (
                        <CloudUpload sx={{ fontSize: 40, color: "rgba(0, 217, 255, 0.5)" }} />
                      )}
                    </Box>
                  </label>
                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)" }}>Click to change photo (max 5MB)</Typography>
                </Box>

                <TextField label="Full Name" value={name} onChange={(e) => setName(e.target.value)} required fullWidth sx={inputStyle} />

                <FormControl fullWidth required sx={inputStyle}>
                  <InputLabel>Position</InputLabel>
                  <Select value={position} onChange={(e) => setPosition(e.target.value)} label="Position"
                    MenuProps={{ PaperProps: { sx: { bgcolor: "#0a192f", "& .MuiMenuItem-root": { color: "white", "&:hover": { bgcolor: "rgba(0, 217, 255, 0.2)" } } } } }}>
                    {positionOptions.map((pos) => (<MenuItem key={pos} value={pos}>{pos}</MenuItem>))}
                  </Select>
                </FormControl>

                <TextField label="Display Order" type="number" value={order} onChange={(e) => setOrder(e.target.value)} required fullWidth inputProps={{ min: 1 }} helperText="Lower numbers appear first" sx={inputStyle} FormHelperTextProps={{ sx: { color: "rgba(255,255,255,0.5)" } }} />

                <Button type="submit" variant="contained" fullWidth disabled={uploading}
                  sx={{ py: 1.5, borderRadius: "12px", background: "linear-gradient(135deg, #10b981, #059669)", fontWeight: 600, fontSize: "1rem", "&:hover": { background: "linear-gradient(135deg, #059669, #047857)" }, "&.Mui-disabled": { background: "rgba(16, 185, 129, 0.3)", color: "rgba(255,255,255,0.5)" } }}>
                  {uploading ? "Updating..." : "Update Executive"}
                </Button>
              </Stack>
            </form>
          </DialogContent>
        </Dialog>

        {/* Feedback Snackbar */}
        <Snackbar open={feedbackOpen} autoHideDuration={4000} onClose={() => setFeedbackOpen(false)} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
          <Alert onClose={() => setFeedbackOpen(false)} severity={feedbackSeverity} sx={{ width: "100%", borderRadius: "12px" }}>{feedbackMessage}</Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default AdminExecutives;
